/**
 * Kinetic dots: a full-screen dot grid that leans towards the pointer (CLAUDE.md §6.8).
 *
 * Colours come from the --dots, --dots-active and --dots-glow tokens, re-read when the theme
 * changes. Sharp on high-density screens, works with touch, holds still under
 * prefers-reduced-motion, and stops redrawing once the dots have settled.
 */
import { useEffect, useRef } from 'react';
import { cn } from '../lib/cn';
import { tokenChannels } from './token-color';

const SPACING = 26;
const INFLUENCE = 500;
const EASING = 0.08;
const MAGNITUDE = 0.3;
const MAGNITUDE_PRESSED = 0.5;
const GROWTH = 0.4;
const SETTLED = 0.01;
const AWAY = -1e4;
/** The original glow's falloff: alpha at each stop, relative to the token's alpha. */
const GLOW_STOPS: readonly (readonly [number, number])[] = [
  [0, 1],
  [0.2, 0.8],
  [0.4, 0.5],
  [0.7, 0.2],
  [1, 0],
];

interface Dot {
  x: number;
  y: number;
  dx: number;
  dy: number;
  scale: number;
  blend: number;
}

interface Palette {
  dot: string;
  active: string;
  glow: [number, number, number, number];
}

function readPalette(): Palette {
  const style = getComputedStyle(document.documentElement);
  const read = (name: string): string => style.getPropertyValue(name).trim();
  return { dot: read('--dots'), active: read('--dots-active'), glow: tokenChannels('--dots-glow') };
}

export function KineticDots({ className }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return undefined;
    const c = context;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');

    let dots: Dot[] = [];
    let palette = readPalette();
    let pointer = { x: AWAY, y: AWAY };
    let magnitude = MAGNITUDE;
    let frame = 0;
    let width = 0;
    let height = 0;

    function layout(): void {
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      c.setTransform(ratio, 0, 0, ratio, 0, 0);
      dots = [];
      for (let y = 0; y <= height + SPACING; y += SPACING) {
        for (let x = 0; x <= width + SPACING; x += SPACING) {
          dots.push({ x, y, dx: 0, dy: 0, scale: 1, blend: 0 });
        }
      }
    }

    /** Moves every dot a step towards its target; true when nothing is moving any more. */
    function step(): boolean {
      let moving = 0;
      for (const dot of dots) {
        const ox = pointer.x - dot.x;
        const oy = pointer.y - dot.y;
        const distance = Math.hypot(ox, oy);
        const pull = distance < INFLUENCE ? (1 - distance / INFLUENCE) ** 2 : 0;
        const tx = ox * pull * magnitude;
        const ty = oy * pull * magnitude;
        const ex = tx - dot.dx;
        const ey = ty - dot.dy;
        const es = 1 + pull * GROWTH - dot.scale;
        const eb = pull - dot.blend;
        dot.dx += ex * EASING;
        dot.dy += ey * EASING;
        dot.scale += es * EASING;
        dot.blend += eb * EASING;
        moving = Math.max(moving, Math.abs(ex), Math.abs(ey), Math.abs(es), Math.abs(eb));
      }
      return moving < SETTLED;
    }

    function draw(): void {
      c.clearRect(0, 0, width, height);
      if (pointer.x > 0 && pointer.y > 0) {
        const [r, g, b, a] = palette.glow;
        const glow = c.createRadialGradient(
          pointer.x,
          pointer.y,
          0,
          pointer.x,
          pointer.y,
          INFLUENCE,
        );
        for (const [offset, factor] of GLOW_STOPS) {
          glow.addColorStop(
            offset,
            `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(a * factor)})`,
          );
        }
        c.fillStyle = glow;
        c.fillRect(0, 0, width, height);
      }
      c.fillStyle = palette.dot;
      c.beginPath();
      for (const dot of dots) {
        const x = dot.x + dot.dx;
        const y = dot.y + dot.dy;
        c.moveTo(x + dot.scale, y);
        c.arc(x, y, dot.scale, 0, Math.PI * 2);
      }
      c.fill();
      // Dots near the pointer take on the accent, in proportion to how close they are.
      c.fillStyle = palette.active;
      for (const dot of dots) {
        if (dot.blend < 0.02) continue;
        c.globalAlpha = dot.blend;
        c.beginPath();
        c.arc(dot.x + dot.dx, dot.y + dot.dy, dot.scale, 0, Math.PI * 2);
        c.fill();
      }
      c.globalAlpha = 1;
    }

    function tick(): void {
      const settled = step();
      draw();
      // Stop once still, even with the pointer resting on the page; the next move wakes it.
      frame = settled ? 0 : requestAnimationFrame(tick);
    }

    function wake(): void {
      if (still.matches) {
        draw();
        return;
      }
      if (frame === 0) frame = requestAnimationFrame(tick);
    }

    function onMove(event: PointerEvent): void {
      if (still.matches) return;
      pointer = { x: event.clientX, y: event.clientY };
      wake();
    }
    function onLeave(): void {
      pointer = { x: AWAY, y: AWAY };
      wake();
    }
    function onDown(event: PointerEvent): void {
      magnitude = MAGNITUDE_PRESSED;
      onMove(event);
    }
    function onUp(event: PointerEvent): void {
      magnitude = MAGNITUDE;
      if (event.pointerType === 'touch') onLeave();
      else wake();
    }
    function onResize(): void {
      layout();
      wake();
    }

    const themeWatch = new MutationObserver(() => {
      palette = readPalette();
      wake();
    });
    themeWatch.observe(document.documentElement, { attributeFilter: ['data-theme'] });

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('blur', onLeave);
    document.documentElement.addEventListener('pointerleave', onLeave);
    window.addEventListener('resize', onResize);
    still.addEventListener('change', onLeave);
    layout();
    draw();

    return () => {
      cancelAnimationFrame(frame);
      themeWatch.disconnect();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('blur', onLeave);
      document.documentElement.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('resize', onResize);
      still.removeEventListener('change', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden
      className={cn('pointer-events-none fixed inset-0 h-full w-full', className)}
    />
  );
}
